import cn from 'classnames';
import React, { useRef, useEffect, FC, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import ReactPlaceholder from 'react-placeholder';
import StudioArrow from 'components/Common/Carousel/StudioRightArrow/StudioArrow';
import OpLightbox from 'components/Common/op-ui-wrappers/OpLightbox/OpLightbox';
import { isNewPublicAsset } from 'store/publicAssetProduct/operation/fetchPublicAssetProductTypesOperation';
import styles from './StudioProductPreviewDesktop.module.scss';

export interface StudioProductPreviewDesktopProps {
  imagePreview: string;
  originalPreviews: string[];
  forcedPreviews: { [index: number]: string };
  onMainImageLoaded: (width: number, height: number) => void;
}

const PREVIEWS_SLIDER_SETTINGS: Settings = {
  accessibility: false,
  arrows: false,
  infinite: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 3,
};

const MAIN_PREVIEW_SLIDER_SETTINGS: Settings = {
  accessibility: false,
  arrows: true,
  infinite: true,
  speed: 500,
  dots: false,
  slidesToShow: 1,
  slidesToScroll: 1,
};

const StudioProductPreviewDesktop: FC<StudioProductPreviewDesktopProps> = ({
  imagePreview,
  originalPreviews,
  forcedPreviews,
  onMainImageLoaded,
}) => {
  const verticalSlider = useRef(null);
  const horizontalSlider = useRef(null);
  const slideIndex = useRef(0);
  const productPreviewIndex = 0;
  const previewSorted = isNewPublicAsset()
    ? [...originalPreviews.slice(0, 1), imagePreview, ...originalPreviews.slice(1)]
    : [imagePreview, ...originalPreviews];

  const mixedPreviews = previewSorted.map((preview, index) => forcedPreviews[index] || preview).slice(0, 8);
  const setGreyBackground = !forcedPreviews?.[0];
  const [activePreview, setActivePreview] = useState<number>(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const gotoSlide = (slideIdx: number) => {
    if (slideIndex.current === slideIdx) {
      return;
    }
    horizontalSlider.current && (horizontalSlider.current as any).slickGoTo(slideIdx);
    verticalSlider.current && (verticalSlider.current as any).slickGoTo(slideIdx);
    slideIndex.current = slideIdx;
    setActivePreview(slideIdx);
  };

  useEffect(() => {
    // if current slide index point to not available preview, slide to first slide
    if (originalPreviews.length && slideIndex.current > originalPreviews.length - 1) {
      gotoSlide(0);
    }
  }, [originalPreviews]);

  useEffect(() => {
    gotoSlide(productPreviewIndex);
  }, [productPreviewIndex]);

  const mainImageLoaded = (e: any) => {
    onMainImageLoaded(e.target.naturalWidth, e.target.naturalHeight);
  };

  return (
    <div className={styles.productPreviewContainer}>
      <OpLightbox
        images={mixedPreviews.map((preview) => ({
          src: preview,
        }))}
        visible={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        zoomEnabled={false}
        currentIndex={activePreview}
      />
      <ReactPlaceholder
        showLoadingAnimation
        type="rect"
        ready={!!imagePreview && mixedPreviews.length > 0}
        style={{ width: '584px', height: '584px', backgroundColor: '#e8e8e8', marginTop: '10px' }}
      >
        <>
          <div className={styles.productPreviews}>
            {mixedPreviews.length > 0 && (
              <Slider
                ref={horizontalSlider}
                afterChange={(currentSlideIdx) => gotoSlide(currentSlideIdx)}
                arrows={MAIN_PREVIEW_SLIDER_SETTINGS.arrows}
                nextArrow={<StudioArrow direction="right" />}
                prevArrow={<StudioArrow direction="left" />}
                accessibility={MAIN_PREVIEW_SLIDER_SETTINGS.accessibility}
                speed={MAIN_PREVIEW_SLIDER_SETTINGS.speed}
                slidesToShow={MAIN_PREVIEW_SLIDER_SETTINGS.slidesToShow}
                slidesToScroll={MAIN_PREVIEW_SLIDER_SETTINGS.slidesToScroll}
                infinite={MAIN_PREVIEW_SLIDER_SETTINGS.infinite}
              >
                {mixedPreviews.map((preview, index) => (
                  <div key={`hs_${preview}`}>
                    <div
                      onClick={() => setIsLightboxOpen(true)}
                      className={cn(styles.productPreview, {
                        [styles.previewSm]: index === 0,
                        [styles.previewShadow]: index === 0 && setGreyBackground,
                        [styles.previewGreyBg]: setGreyBackground,
                      })}
                    >
                      <img src={preview} alt="" onLoad={index === 0 ? mainImageLoaded : undefined} />
                    </div>
                  </div>
                ))}
              </Slider>
            )}
          </div>
          <div className={styles.productThumbnails}>
            <Slider
              ref={verticalSlider}
              accessibility={PREVIEWS_SLIDER_SETTINGS.accessibility}
              arrows={PREVIEWS_SLIDER_SETTINGS.arrows}
              infinite={PREVIEWS_SLIDER_SETTINGS.infinite}
              speed={PREVIEWS_SLIDER_SETTINGS.speed}
              slidesToShow={PREVIEWS_SLIDER_SETTINGS.slidesToShow}
              slidesToScroll={PREVIEWS_SLIDER_SETTINGS.slidesToScroll}
            >
              {mixedPreviews.map((preview, index) => (
                <div key={`vs_${index}`} onClick={() => gotoSlide(index)}>
                  <div
                    className={cn(styles.productThumbnail, {
                      [styles.previewSm]: index === 0,
                      [styles.previewShadow]: index === 0 && setGreyBackground,
                      [styles.selected]: index === activePreview,
                      [styles.previewGreyBg]: setGreyBackground,
                    })}
                  >
                    <img itemProp="image" src={preview} alt="" />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </>
      </ReactPlaceholder>
    </div>
  );
};

export default StudioProductPreviewDesktop;
